import { palette } from '@client/theme/colors';
import { theme } from '@client/theme/constants';
import { shadows } from '@client/theme/shadows';
import type { Theme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material';

export const generateBackdropStyles = (defaultTheme: Theme) => ({
  colorInherit: {
    backgroundColor: defaultTheme.palette.background.paper,
  },
  colorPrimary: {
    backgroundColor: defaultTheme.palette.primary.xlight,

    '& :not(.MuiListItemText-root) > .MuiTypography-root:not(.MuiLink-root)': {
      color: defaultTheme.palette.primary.dark,
    },

    '& :not(.MuiListItemText-root) > .MuiTypography-root.MuiLink-root, & :not(.MuiListItemIcon-root) > svg': {
      color: defaultTheme.palette.primary.main,
    },
  },
  colorSecondary: {
    backgroundColor: defaultTheme.palette.secondary.xlight,

    '& :not(.MuiListItemText-root) > .MuiTypography-root:not(.MuiLink-root)': {
      color: defaultTheme.palette.secondary.dark,
    },

    '& :not(.MuiListItemText-root) > .MuiTypography-root.MuiLink-root, & :not(.MuiListItemIcon-root) > svg': {
      color: defaultTheme.palette.secondary.main,
    },
  },
  colorSuccess: {
    backgroundColor: defaultTheme.palette.success.xlight,

    '& :not(.MuiListItemText-root) > .MuiTypography-root:not(.MuiLink-root)': {
      color: defaultTheme.palette.success.dark,
    },

    '& :not(.MuiListItemText-root) > .MuiTypography-root.MuiLink-root, & :not(.MuiListItemIcon-root) > svg': {
      color: defaultTheme.palette.success.main,
    },
  },
  colorError: {
    backgroundColor: defaultTheme.palette.error.xlight,

    '& :not(.MuiListItemText-root) > .MuiTypography-root:not(.MuiLink-root)': {
      color: defaultTheme.palette.error.dark,
    },

    '& :not(.MuiListItemText-root) > .MuiTypography-root.MuiLink-root, & :not(.MuiListItemIcon-root) > svg': {
      color: defaultTheme.palette.error.main,
    },
  },
  colorWarning: {
    backgroundColor: defaultTheme.palette.warning.xlight,

    '& :not(.MuiListItemText-root) > .MuiTypography-root:not(.MuiLink-root)': {
      color: defaultTheme.palette.error.dark, // Yes, use the error colour here - this follows our designs
    },

    '& :not(.MuiListItemText-root) > .MuiTypography-root.MuiLink-root, & :not(.MuiListItemIcon-root) > svg': {
      color: defaultTheme.palette.warning.main,
    },
  },
  colorInfo: {
    backgroundColor: defaultTheme.palette.info.xlight,

    '& :not(.MuiListItemText-root) > .MuiTypography-root:not(.MuiLink-root)': {
      color: defaultTheme.palette.info.dark,
    },

    '& :not(.MuiListItemText-root) > .MuiTypography-root.MuiLink-root, & :not(.MuiListItemIcon-root) > svg': {
      color: defaultTheme.palette.info.main,
    },
  },
});

const Card: ThemeOptions['components'] = {
  MuiCard: {
    defaultProps: {
      elevation: 0, // set default as fallback
    },
  },
  MuiCardActions: {
    styleOverrides: {
      spacing: {
        '& > * + *': {
          marginLeft: `${theme.spacing(1)} !important`,
        },
      },
    },
  },
  MuiCardActionArea: {
    styleOverrides: {
      focusHighlight: {
        backgroundColor: palette.grey[300],

        display: 'none !important',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        position: 'relative',
        whiteSpace: 'pre-wrap',
        padding: theme.spacing(2),

        '&:last-child': {
          paddingBottom: theme.spacing(2),
        },
      },
    },
  },
  MuiCardMedia: {
    styleOverrides: {
      root: {
        height: 300,
        backgroundPosition: 'top',
      },
    },
  },
  MuiCardHeader: {
    defaultProps: {
      subheaderTypographyProps: {
        variant: 'body1',
        color: 'textPrimary',
      },
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      elevation4: { // For making cards with a lighter shadow
        boxShadow: shadows[4],
      },
      rounded: {
        borderRadius: 7,
      },
      outlined: {
        border: `2px solid ${palette.error.dark}`,
        boxShadow: shadows[4],
        overflow: 'visible !important',
      },
      elevation23: { // For making cards with black backgrounds
        borderRadius: 0,
        backgroundColor: palette.grey[700],
        color: `${palette.grey[50]} !important`,

        '& *:not(a):not(button) > span, & a:not([role="button"]), & svg': {
          color: palette.grey[50],
        },

        '& li': {
          color: palette.grey[50],
        },

        '& hr': {
          backgroundColor: palette.text.primary,
        },

        '&.MuiListItemText-secondary': {
          color: palette.grey[50],
          fontWeight: 400,
        },
      },
    },
  },
};

export default Card;
